import { storage, storageKeys } from '@/constants/storage';
import { DevLogger, LogLevel } from '@/utils/logging/loggers/devLogger';

import type { LoggerContext } from './_types';
import { LoggerHandler } from './loggerHandler';
// @ts-expect-error - DatadogLogger is defined in two files for each platform
import { DatadogLogger } from './loggers/datadogLogger';
import { SentryLogger } from './loggers/sentryLogger';

const loggerHandler = LoggerHandler();

export const Logger = {
  initialize() {
    // In __DEV__ mode (local development), ONLY use console logging
    // Never send debug/info logs to Sentry/Datadog in development
    if (__DEV__) {
      loggerHandler.addLogger('devLogger', DevLogger(LogLevel.debug));
      return;
    }

    // Only use Sentry/Datadog in production builds
    switch (process.env.EXPO_PUBLIC_RUNTIME_ENV) {
      case 'development':
        loggerHandler.addLogger('devLogger', DevLogger(LogLevel.info));
        break;
      case 'qa':
        loggerHandler.addLogger('sentryLogger', SentryLogger());
        loggerHandler.addLogger('datadogLogger', DatadogLogger());
        break;
      case 'production':
        loggerHandler.addLogger('sentryLogger', SentryLogger());
        loggerHandler.addLogger('datadogLogger', DatadogLogger());
        break;
    }
  },
  info(message: string, properties?: LoggerContext) {
    loggerHandler.info?.(message, {
      ...properties,
      anonymousUserId: storage.getString(storageKeys.rudderstackAnonymusUserId),
      clarity_session_url: storage.getString(storageKeys.trackingSessionUrl),
    });
  },
  /** Local `__DEV__` console only; never reaches Datadog or Sentry. */
  console(message: string, context?: LoggerContext) {
    if (__DEV__) {
      loggerHandler.debug?.(message, context);
    }
  },
  debug(message: string, context?: LoggerContext) {
    loggerHandler.debug?.(message, {
      ...context,
      anonymousUserId: storage.getString(storageKeys.rudderstackAnonymusUserId),
      clarity_session_url: storage.getString(storageKeys.trackingSessionUrl),
    });
  },
  warn(message: string, context?: LoggerContext) {
    loggerHandler.warn?.(message, {
      ...context,
      anonymousUserId: storage.getString(storageKeys.rudderstackAnonymusUserId),
      clarity_session_url: storage.getString(storageKeys.trackingSessionUrl),
    });
  },
  error(message: string, context?: LoggerContext) {
    loggerHandler.error?.(message, {
      ...context,
      anonymousUserId: storage.getString(storageKeys.rudderstackAnonymusUserId),
      clarity_session_url: storage.getString(storageKeys.trackingSessionUrl),
    });
  },
};
